/**
 * A tool string's items, top to bottom, and the editor that replaces them
 * (PUT /wireline-slickline/tool-strings/:id/items — the API replaces the
 * WHOLE list in the order sent, at most 100).
 *
 * Read: the items as the API returned them, in its `position` order, each in
 * its entered units. Edit (WL_GEAR_WRITE): rows can be added, removed and
 * moved; Save sends the full list. Equipment is only offered for an item we
 * own (the API refuses it on an operator's or rented item).
 *
 * Checked before sending, each with a sentence: a component and an owner on
 * every row, positive numbers. The API checks them again; its sentence is
 * shown as sent.
 */
import React, { useState } from 'react';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useReplaceWlToolStringItems, useWlEquipmentOptions } from '../../hooks/useWirelineSlickline';
import { wlBlankToNull, wlCatalogLabel, wlRefusal, wlToInput, wlWithUnit } from '../../lib/wireline-slickline';
import type { WlCatalog, WlMeta, WlToolString, WlToolStringItemWrite } from '../../types/wireline-slickline';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WL_SMALL_BTN, WlEmpty, WlFormError, WlPanel } from './WlBits';

interface ItemRow {
  key: string;
  component_kind: string;
  description: string;
  od: string;
  od_unit: string;
  length: string;
  length_unit: string;
  owner: string;
  equipment_id: string;
}

let rowSeq = 0;
const nextKey = () => `row-${(rowSeq += 1)}`;

const POSITIVE = /^\d+(\.\d+)?$/;
export const WL_MAX_TOOL_ITEMS = 100;

/** The first thing wrong with the rows, as a sentence; null when they may be sent. */
export function itemsError(rows: ItemRow[]): string | null {
  if (rows.length > WL_MAX_TOOL_ITEMS) return `A tool string holds at most ${WL_MAX_TOOL_ITEMS} items.`;
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const n = i + 1;
    if (!r.component_kind) return `Item ${n}: pick the component.`;
    if (!r.owner) return `Item ${n}: say whose it is.`;
    if (r.od.trim() !== '' && (!POSITIVE.test(r.od.trim()) || Number(r.od) <= 0)) return `Item ${n}: OD must be a number greater than 0.`;
    if (r.length.trim() !== '' && (!POSITIVE.test(r.length.trim()) || Number(r.length) <= 0)) return `Item ${n}: length must be a number greater than 0.`;
  }
  return null;
}

export function itemsBody(rows: ItemRow[]): WlToolStringItemWrite[] {
  return rows.map((r) => {
    const out: WlToolStringItemWrite = { component_kind: r.component_kind, owner: r.owner };
    out.description = wlBlankToNull(r.description);
    if (r.od.trim() !== '') {
      out.od = Number(r.od.trim());
      out.od_unit = r.od_unit || null;
    }
    if (r.length.trim() !== '') {
      out.length = Number(r.length.trim());
      out.length_unit = r.length_unit || null;
    }
    out.equipment_id = r.owner === 'ours' && r.equipment_id ? r.equipment_id : null;
    return out;
  });
}

export function ToolStringItems({
  toolString,
  catalog,
  meta,
  canWrite,
}: {
  toolString: WlToolString;
  catalog: WlCatalog | null;
  meta: WlMeta | null;
  canWrite: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<ItemRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const replace = useReplaceWlToolStringItems();
  const equipment = useWlEquipmentOptions(editing);
  const diameterUnit = meta?.units?.diameter ?? '';
  const lengthUnit = meta?.units?.length ?? '';
  const diameterUnits = catalog?.diameterUnits?.length ? catalog.diameterUnits : diameterUnit ? [diameterUnit] : [];
  const lengthUnits = catalog?.lengthUnits?.length ? catalog.lengthUnits : lengthUnit ? [lengthUnit] : [];
  const items = toolString.items ?? [];

  const start = () => {
    setRows(
      items.map((i) => ({
        key: nextKey(),
        component_kind: i.componentKind,
        description: i.description ?? '',
        od: wlToInput(i.od),
        od_unit: i.odUnit ?? diameterUnit,
        length: wlToInput(i.length),
        length_unit: i.lengthUnit ?? lengthUnit,
        owner: i.owner,
        equipment_id: i.equipmentId ?? '',
      })),
    );
    setError(null);
    setEditing(true);
  };
  const setRow = (key: string, patch: Partial<ItemRow>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const move = (idx: number, by: number) =>
    setRows((rs) => {
      const to = idx + by;
      if (to < 0 || to >= rs.length) return rs;
      const out = [...rs];
      [out[idx], out[to]] = [out[to], out[idx]];
      return out;
    });
  const addRow = () =>
    setRows((rs) => [
      ...rs,
      { key: nextKey(), component_kind: '', description: '', od: '', od_unit: diameterUnit, length: '', length_unit: lengthUnit, owner: 'ours', equipment_id: '' },
    ]);

  const save = () => {
    setError(null);
    const problem = itemsError(rows);
    if (problem) return setError(problem);
    replace.mutate(
      { id: toolString.id, items: itemsBody(rows) },
      {
        onSuccess: () => {
          toast.success('Items saved');
          setEditing(false);
        },
        onError: (err) => setError(wlRefusal(err, 'The items could not be saved.')),
      },
    );
  };

  return (
    <WlPanel
      eyebrow="Top to bottom"
      title="Items"
      testId="wl-tool-string-items"
      actions={
        canWrite && !editing ? (
          <button type="button" className={WL_SMALL_BTN} onClick={start}>
            <Pencil className="size-3.5" aria-hidden="true" />
            Edit items
          </button>
        ) : undefined
      }
    >
      {!editing ? (
        items.length === 0 ? (
          <WlEmpty>No items yet.{canWrite ? ' Edit items to build the string.' : ''}</WlEmpty>
        ) : (
          <ol className="divide-y divide-fieldiq-border">
            {items.map((i, idx) => (
              <li key={i.id} className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-2" data-testid="wl-tool-item">
                <div className="min-w-0">
                  <span className="mr-2 font-mono text-xs text-fieldiq-muted">{idx + 1}</span>
                  <span className="font-semibold text-white">{i.componentKindLabel ?? wlCatalogLabel(catalog?.toolComponentKinds, i.componentKind)}</span>
                  {i.description && <span className="ml-2 text-sm text-fieldiq-text">{i.description}</span>}
                </div>
                <div className="font-mono text-xs text-fieldiq-text">
                  OD {wlWithUnit(i.od, i.odUnit)} · Length {wlWithUnit(i.length, i.lengthUnit)} · {i.ownerLabel ?? wlCatalogLabel(catalog?.toolOwners, i.owner)}
                  {i.equipmentName ? ` · ${i.equipmentName}` : ''}
                </div>
              </li>
            ))}
          </ol>
        )
      ) : (
        <div className="space-y-3 px-4 py-4" data-testid="wl-items-editor">
          <WlFormError message={error} />
          {rows.length === 0 && <p className="text-sm text-fieldiq-muted">No items. Saving an empty list clears the string.</p>}
          {rows.map((r, idx) => (
            <div key={r.key} className="grid gap-2 rounded-fi-control border border-fieldiq-border p-3 lg:grid-cols-[2rem_minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1fr)_auto]" data-testid="wl-item-row">
              <span className="self-center font-mono text-xs text-fieldiq-muted">{idx + 1}</span>
              <select className={WL_INPUT} value={r.component_kind} onChange={(e) => setRow(r.key, { component_kind: e.target.value })} aria-label={`Item ${idx + 1} component`}>
                <option value="">Component…</option>
                {(catalog?.toolComponentKinds ?? []).map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
              <input className={WL_INPUT} placeholder="Description" value={r.description} onChange={(e) => setRow(r.key, { description: e.target.value })} aria-label={`Item ${idx + 1} description`} />
              <div className="flex gap-1">
                <input className={WL_INPUT} placeholder="OD" inputMode="decimal" value={r.od} onChange={(e) => setRow(r.key, { od: e.target.value })} aria-label={`Item ${idx + 1} OD`} />
                <select className={`${WL_INPUT} w-16 shrink-0`} value={r.od_unit} onChange={(e) => setRow(r.key, { od_unit: e.target.value })} aria-label={`Item ${idx + 1} OD unit`}>
                  {diameterUnits.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-1">
                <input className={WL_INPUT} placeholder="Length" inputMode="decimal" value={r.length} onChange={(e) => setRow(r.key, { length: e.target.value })} aria-label={`Item ${idx + 1} length`} />
                <select className={`${WL_INPUT} w-16 shrink-0`} value={r.length_unit} onChange={(e) => setRow(r.key, { length_unit: e.target.value })} aria-label={`Item ${idx + 1} length unit`}>
                  {lengthUnits.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              <select className={WL_INPUT} value={r.owner} onChange={(e) => setRow(r.key, { owner: e.target.value })} aria-label={`Item ${idx + 1} owner`}>
                <option value="">Owner…</option>
                {(catalog?.toolOwners ?? []).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <select
                className={WL_INPUT}
                value={r.equipment_id}
                disabled={r.owner !== 'ours'}
                onChange={(e) => setRow(r.key, { equipment_id: e.target.value })}
                aria-label={`Item ${idx + 1} equipment`}
              >
                <option value="">{r.owner === 'ours' ? 'No equipment link' : 'Ours only'}</option>
                {(equipment.data ?? []).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                    {x.unitNumber ? ` · ${x.unitNumber}` : ''}
                  </option>
                ))}
              </select>
              <div className="flex gap-1">
                <button type="button" className={WL_SMALL_BTN} onClick={() => move(idx, -1)} disabled={idx === 0} aria-label={`Move item ${idx + 1} up`}>
                  <ArrowUp className="size-3.5" aria-hidden="true" />
                </button>
                <button type="button" className={WL_SMALL_BTN} onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} aria-label={`Move item ${idx + 1} down`}>
                  <ArrowDown className="size-3.5" aria-hidden="true" />
                </button>
                <button type="button" className={WL_SMALL_BTN} onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} aria-label={`Remove item ${idx + 1}`}>
                  <Trash2 className="size-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap justify-between gap-2">
            <button type="button" className={WL_SMALL_BTN} onClick={addRow} disabled={rows.length >= WL_MAX_TOOL_ITEMS}>
              <Plus className="size-3.5" aria-hidden="true" />
              Add item
            </button>
            <div className="flex gap-2">
              <button type="button" className={WL_SECONDARY} onClick={() => setEditing(false)} disabled={replace.isPending}>
                Cancel
              </button>
              <button type="button" className={WL_PRIMARY} onClick={save} disabled={replace.isPending}>
                {replace.isPending ? 'Saving…' : 'Save items'}
              </button>
            </div>
          </div>
        </div>
      )}
    </WlPanel>
  );
}
